/**
 * Shopify Store API - fetches Shopify store data and configuration.
 * Base URL: set VITE_API_BASE_URL in .env
 * Endpoints:
 *   GET /bot/api/shopify/connected - check if Shopify is connected
 *   GET /bot/api/shopify/store - fetch store information
 *   POST /bot/api/shopify/store/bot-settings - update bot settings
 *   GET /bot/api/shopify/discounts/summary - fetch discount summary
 *   POST /bot/api/shopify/discounts/exclusions - update discount exclusions
 */

import { getBaseUrl, parseJson, buildUrl } from '../utils.js';
import { getAuthHeaders } from '../auth';

/**
 * Check if Shopify store is connected.
 * GET /bot/api/shopify/connected
 * @returns {Promise<{ connected: boolean }>}
 */
export async function isShopifyConnected() {
  try {
    const base = getBaseUrl();
    const url = buildUrl(base, 'bot/api/shopify/connected');

    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json', ...getAuthHeaders() },
    });

    const data = await parseJson(res);
    return data;
  } catch (err) {
    console.error('Shopify connected check failed:', err);
    throw err;
  }
}

/**
 * Fetch Shopify store information.
 * GET /bot/api/shopify/store
 * @returns {Promise<{ 
 *   store_name: string, 
 *   store_url: string, 
 *   created_at: string | null,
 *   bot_settings: {
 *     show_exact_prices: boolean,
 *     show_stock_levels: boolean,
 *     product_recommendations: boolean,
 *     proactive_discount_sharing: boolean,
 *     discount_exclusions: string[] | null,
 *     quick_reply_buttons: Array,
 *     welcome_heading: string | null
 *   }
 * }>}
 */
export async function getShopifyStore() {
  try {
    const base = getBaseUrl();
    const url = buildUrl(base, 'bot/api/shopify/store');
    
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json', ...getAuthHeaders() },
    });

    const data = await parseJson(res);
    return data;
  } catch (err) {
    console.error('Shopify store API failed:', err);
    throw err;
  }
}

/**
 * Fetch real-time store counts from Shopify.
 * GET /bot/api/shopify/store/counts
 * @returns {Promise<{ products: number, collections: number, discounts: number }>}
 */
export async function getStoreCounts() {
  const base = getBaseUrl();
  const url = buildUrl(base, 'bot/api/shopify/store/counts');
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Update bot settings.
 * POST /bot/api/shopify/store/bot-settings
 * @param {object} settings - Bot settings to update
 * @param {boolean} [settings.show_exact_prices] - Show exact prices
 * @param {boolean} [settings.show_stock_levels] - Show stock levels
 * @param {boolean} [settings.product_recommendations] - Product recommendations
 * @param {boolean} [settings.proactive_discount_sharing] - Proactive discount sharing
 * @returns {Promise<{ success: boolean, message: string }>}
 */
export async function updateBotSettings(settings) {
  try {
    const base = getBaseUrl();
    const url = buildUrl(base, 'bot/api/shopify/store/bot-settings');
    
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(settings),
    });
    
    const data = await parseJson(res);
    return data;
  } catch (err) {
    console.error('Update bot settings API failed:', err);
    throw err;
  }
}

/**
 * Fetch discount summary.
 * GET /bot/api/shopify/discounts/summary
 * @returns {Promise<{
 *   success: boolean,
 *   data?: {
 *     discounts: Array<{ id: string, type: string, title: string, summary: string }>,
 *     excluded_discount_ids: string[]
 *   }
 * }>}
 * By default all discounts are included; excluded_discount_ids are those disabled (hidden from AI).
 */
export async function getDiscountSummary() {
  try {
    const base = getBaseUrl();
    const url = buildUrl(base, 'bot/api/shopify/discounts/summary');
    
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json', ...getAuthHeaders() },
    });

    const json = await parseJson(res);
    return json;
  } catch (err) {
    console.error('Discount summary API failed:', err);
    throw err;
  }
}

/**
 * Update discount exclusions.
 * POST /bot/api/shopify/discounts/exclusions
 * @param {string} discountId - The discount ID to add or remove
 * @param {string} action - Either "add" or "remove"
 * @returns {Promise<{
 *   excluded_discount_ids: string[]
 * }>}
 */
export async function updateDiscountExclusions(discountId, action) {
  try {
    const base = getBaseUrl();
    const url = buildUrl(base, 'bot/api/shopify/discounts/exclusions');
    
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        discount_id: discountId,
        action: action
      }),
    });
    
    const data = await parseJson(res);
    return data;
  } catch (err) {
    console.error('Update discount exclusions API failed:', err);
    throw err;
  }
}
